import { AGENT_META } from "./agents";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PREFILLS = {
  SALES_AGENT: { task: "qualify_lead", field: "lead_id" },
  PROPOSALS_AGENT: { task: "draft_proposal", field: "opportunity_id" },
  MEETINGS_AGENT: { task: "follow_up_review", field: "lead_id" },
};

export const agentPath = (agentId) =>
  agentId && ["CONTENT_AGENT", "INTELLIGENCE_AGENT", "SALES_AGENT", "PROPOSALS_AGENT", "MEETINGS_AGENT", "COMMERCE_AGENT"].includes(agentId) && AGENT_META[agentId] ? `/app/agents/${agentId}` : null;

export const crmRecordPath = (type, id) => {
  if (!id) return null;
  if (type === "lead") return `/app/crm?tab=leads&record=lead:${encodeURIComponent(id)}`;
  if (type === "opportunity") return `/app/crm?tab=pipeline&record=opportunity:${encodeURIComponent(id)}`;
  return null;
};

const COMPANY_MEMORY_TABS = { contact: "contacts", company: "companies", lead: "leads", opportunity: "pipeline" };
const companyMemoryRecordPath = (type, id) =>
  typeof id === "string" && UUID.test(id) && Object.hasOwn(COMPANY_MEMORY_TABS, type)
    ? `/app/crm?tab=${COMPANY_MEMORY_TABS[type]}&record=${type}:${encodeURIComponent(id)}`
    : null;

// References are untrusted. Only existing CRM destinations and valid UUIDs may become links.
export const companyMemorySourcePath = (source) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) return null;
  const type = source.source_type;
  if (typeof type !== "string") return null;
  if (Object.hasOwn(COMPANY_MEMORY_TABS, type)) return companyMemoryRecordPath(type, source.source_id);
  if (["note", "interaction", "email_thread", "email_message"].includes(type)) {
    for (const parent of ["opportunity", "lead", "contact"]) {
      const path = companyMemoryRecordPath(parent, source[`${parent}_id`]);
      if (path) return path;
    }
  }
  return null;
};
export const approvalPath = (id) => id ? `/app/approvals?status=all&item=${encodeURIComponent(id)}` : null;
export const contentPath = (id) => id ? `/app/social?item=${encodeURIComponent(id)}` : null;
export const runPath = (id) => id ? `/app/ai?tab=runs&run=${encodeURIComponent(id)}` : null;

export const aiTabParams = (current, tab, extra = {}) => {
  const next = new URLSearchParams(current);
  next.set("tab", tab);
  Object.entries(extra).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
  if (!Object.hasOwn(extra, "run") && tab !== "runs" && tab !== "logs") next.delete("run");
  return next;
};

export const agentPrefillPath = (agentId, field, id) => {
  const rule = PREFILLS[agentId];
  if (!rule || rule.field !== field || !UUID.test(id || "")) return null;
  return `${agentPath(agentId)}?${new URLSearchParams({ task: rule.task, [field]: id })}`;
};

export const readAgentPrefill = (agentId, search) => {
  const rule = PREFILLS[agentId];
  if (!rule) return null;
  const params = new URLSearchParams(search);
  const id = params.get(rule.field);
  if (params.get("task") !== rule.task || !UUID.test(id || "")) return null;
  return { taskType: rule.task, values: { [rule.field]: id }, key: `${rule.task}:${id}` };
};

export const approvalSourcePath = (approval) => {
  if (!approval?.source_id) return null;
  if (approval.source_type === "content_item") return contentPath(approval.source_id);
  if (approval.source_type === "lead" || approval.source_type === "opportunity") return crmRecordPath(approval.source_type, approval.source_id);
  // Proposal approval metadata has no related CRM id; its source id is a proposal, not an opportunity.
  return null;
};

export const artifactPath = (artifact) => {
  if (!artifact) return null;
  if (artifact.type === "approval") return approvalPath(artifact.id);
  if (artifact.type === "content_item") return contentPath(artifact.id);
  if (artifact.type === "lead" || artifact.type === "opportunity") return crmRecordPath(artifact.type, artifact.id);
  if (artifact.approval_id && ["proposal", "sales_message"].includes(artifact.type)) return approvalPath(artifact.approval_id);
  return null;
};